import {
  AnnounceRequestError,
  type PublicAnnouncement,
} from "@/features/announce/api/announceClient";
import type { AnnouncementEvent } from "@/features/announce/mediators/announcementEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type AnnouncementModalEvent = Extract<
  AnnouncementEvent,
  {
    type:
      | "announcement/detail-opened"
      | "announcement/detail-retried"
      | "announcement/list-retried"
      | "announcement/modal-closed"
      | "announcement/modal-list-requested"
      | "announcement/modal-page-requested"
      | "announcement/modal-initial-synced";
  }
>;

export function isAnnouncementModalEvent(
  event: AnnouncementEvent,
): event is AnnouncementModalEvent {
  return (
    event.type === "announcement/detail-opened" ||
    event.type === "announcement/detail-retried" ||
    event.type === "announcement/list-retried" ||
    event.type === "announcement/modal-closed" ||
    event.type === "announcement/modal-list-requested" ||
    event.type === "announcement/modal-page-requested" ||
    event.type === "announcement/modal-initial-synced"
  );
}

export type AnnouncementModalView =
  | { readonly name: "list"; readonly focusAnnouncementId?: number }
  | { readonly name: "detail"; readonly announcement: PublicAnnouncement }
  | { readonly name: "initialDetailError"; readonly error: unknown };

export interface AnnouncementModalState {
  readonly handledInitialAnnouncementId: number | null;
  readonly view: AnnouncementModalView;
}

export type AnnouncementModalEffect =
  | { readonly type: "close" }
  | { readonly type: "open-page"; readonly announcementId?: number }
  | { readonly type: "retry-list" }
  | { readonly type: "retry-initial" };

export function createAnnouncementModalMediator(
  runEffect: (effect: AnnouncementModalEffect) => void,
): Mediator<
  AnnouncementModalState,
  AnnouncementModalEvent,
  AnnouncementModalEffect
> {
  return createMediator<
    AnnouncementModalState,
    AnnouncementModalEvent,
    AnnouncementModalEffect
  >(
    { handledInitialAnnouncementId: null, view: { name: "list" } },
    (state, event) => {
      switch (event.type) {
        case "announcement/detail-opened":
          return {
            status: "handled",
            state: {
              ...state,
              view: { name: "detail", announcement: event.announcement },
            },
          };
        case "announcement/modal-list-requested":
          return {
            status: "handled",
            state: {
              ...state,
              view: {
                name: "list",
                focusAnnouncementId: event.focusAnnouncementId,
              },
            },
          };
        case "announcement/modal-closed":
          return {
            status: "handled",
            state: { ...state, view: { name: "list" } },
            effects: [{ type: "close" }],
          };
        case "announcement/modal-page-requested":
          return {
            status: "handled",
            state: { ...state, view: { name: "list" } },
            effects: event.nativeNavigation
              ? [{ type: "close" }]
              : [
                  { type: "close" },
                  {
                    type: "open-page",
                    announcementId: event.announcementId,
                  },
                ],
          };
        case "announcement/list-retried":
          return {
            status: "handled",
            state,
            effects: [{ type: "retry-list" }],
          };
        case "announcement/detail-retried":
          return {
            status: "handled",
            state: { ...state, view: { name: "list" } },
            effects: [{ type: "retry-initial" }],
          };
        case "announcement/modal-initial-synced":
          return resolveInitialAnnouncement(state, event);
      }
    },
    { runEffect },
  );
}

function resolveInitialAnnouncement(
  state: AnnouncementModalState,
  event: Extract<
    AnnouncementModalEvent,
    { type: "announcement/modal-initial-synced" }
  >,
) {
  if (
    event.initialAnnouncementId === undefined ||
    state.handledInitialAnnouncementId === event.initialAnnouncementId ||
    event.isLoading
  ) {
    return { status: "handled" as const, state };
  }
  if (
    event.error instanceof AnnounceRequestError &&
    event.error.kind === "not_found"
  ) {
    return {
      status: "handled" as const,
      state: {
        handledInitialAnnouncementId: event.initialAnnouncementId,
        view: { name: "list" } as const,
      },
    };
  }
  const announcement = event.announcement ?? event.listedAnnouncement;
  if (announcement) {
    return {
      status: "handled" as const,
      state: {
        handledInitialAnnouncementId: event.initialAnnouncementId,
        view: { name: "detail", announcement } as const,
      },
    };
  }
  if (event.error) {
    return {
      status: "handled" as const,
      state: {
        ...state,
        view: { name: "initialDetailError", error: event.error } as const,
      },
    };
  }
  return {
    status: "handled" as const,
    state: { ...state, view: { name: "list" } as const },
  };
}
